import { useUserData, withUserData } from "@/contexts/UserDataContext";
import { GetProfile } from "@/utils/admin/ssr/profile";
import PageViewProfile, { getCurrentPageProfile } from "@/components/profile/view/PageViewProfile";
import "@fontsource/raleway/400.css";
import { DEFAULT_PFP_URL, IS_LOCKED_LOADING } from "@/utils/profile";
import { useEffect, useState } from "react";
import { useAPI } from "@/contexts/APIContext";
import { find } from "@/utils/data/colleges-data";
import admin from "@/utils/admin/firebase-admin-init";
import { useRouter } from "next/router";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import ModalAuth from "@/components/user/ModalAuth";
import { captureFB, captureReddit, logEvent } from "@/utils/analytics";
import { ModalFreeUnlockProfile } from "@/components/plans/ModalFreeUnlockEligible";
import Head from "next/head";
import CollegeAdvisorModal from "@/components/collegeadvisor/CollegeAdvisorModal";
import { isDev, isProductionBuild } from "@/utils/data/constants";

const ProfileLoadingWrapper = ({
  profileData: initialProfileData,
  locked: initialLocked,
  ...rest
}) => {
  const { currentUser, refreshUserData } = useUserData();
  const router = useRouter();
  const api = useAPI();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showFreeUnlockModal, setShowFreeUnlockModal] = useState(false);
  const [locked, setLocked] = useState(initialLocked);
  const [profileData, setProfileData] = useState(initialProfileData);

  const id = getCurrentPageProfile(router);

  const loadProfile = async () => {
    if (currentUser === null) {
      // signed out
      // keep the locked state if it was already set, replacing "loading" with true
      // for some profiles though like Ananth's it's always unlocked
      if (!locked) {
        setTimeout(() => setShowAuthModal(true), 10000);
      } else {
        setTimeout(() => setShowFreeUnlockModal(true), 10000);
      }
      setLocked(!!locked);
    } else if (!!currentUser) {
      // signed in
      refreshUserData(); // do this in the background
      const response = await api.get("profiles/" + id, true);
      logEvent(api, "viewProfile", {
        profileId: id,
        profileName: response.profileData.display_name,
        profileNColleges: response.profileData.colleges.length,
        profileLocked: response.locked,
      });
      setProfileData(response.profileData);
      setLocked(response.locked);
    }
    captureFB("ViewContent");
    captureReddit("ViewContent");
  };

  useEffect(() => {
    loadProfile();
  }, [currentUser]);

  if (!profileData) {
    return null;
  }

  return (
    <>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>

      <PageViewProfile profileData={profileData} locked={locked} {...rest} />

      <CollegeAdvisorModal />

      <ModalAuth
        size="lg"
        open={showAuthModal}
        header="To keep reading this application, make a free account with us!"
        onComplete={() => setShowAuthModal(false)}
      />
      <ModalFreeUnlockProfile
        open={showFreeUnlockModal}
        onClose={() => setShowFreeUnlockModal(false)}
      />
    </>
  );
};

export default withUserData(ProfileLoadingWrapper);

export const getStaticProps = async (context) => {
  const profileProps = await GetProfile(context);
  const { profileData: profile } = profileProps;
  if (!profile.created) {
    return {
      notFound: true,
    };
  }

  let pfpURL = DEFAULT_PFP_URL;
  try {
    pfpURL = await getDownloadURL(ref(getStorage(), `pfps/${context.params.id}.jpg`));
  } catch (e) {
    // Ignore the exception - just means they have a default profile pic
  }
  const top3Colleges = profile.colleges
    .filter((c) => c.result === "Accepted")
    .sort((a, b) => find(a.name)?.rank - find(b.name)?.rank)
    .map((college) => find(college.name)?.name)
    .slice(0, 3)
    .join(";");
  const ogImageURL =
    `/api/og/profile?name=${encodeURIComponent(profile.display_name)}` +
    `&pfp=${encodeURIComponent(pfpURL)}` +
    `&ecs=${profile.ecs.length}` +
    `&essays=${profile.essays.length}` +
    `&universities=${encodeURIComponent(top3Colleges)}`;

  delete profile.updated;
  delete profile.created;

  return {
    props: {
      profileData: profile,
      locked: !profileProps.locked ? false : IS_LOCKED_LOADING,
      ogImageURL,
    },
    revalidate: 60 * 60 * 24 * 7, // once a week
  };
};
export const getStaticPaths = async () => {
  let paths = [];

  // Test if it's being built locally or on Vercel
  const isVercel = !!process.env.VERCEL_ENV;

  if (isVercel && isProductionBuild) {
    // Statically render all profiles that have been unlocked at least once - these are HQ ones
    const unlocksByProfile = (
      await admin.firestore().doc("analytics/unlocksByProfile").get()
    ).data();
    paths = Object.keys(unlocksByProfile).map((id) => ({ params: { id } }));
  }

  return {
    paths,
    fallback: "blocking",
  };
};
